import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Sliders, Power, Sparkles, Gauge, Sun } from "lucide-react";
import { toast } from "sonner";
import { formatDeviceId } from "@/utils/deviceUtils";

type DeviceRow = {
  device_id: string;
  name: string | null;
  location: string | null;
  hardware_type: string | null;
};

type CommandType =
  | "set_brightness"
  | "set_speed"
  | "set_power"
  | "set_accent";

/**
 * Web replacement for the two physical encoders on slave devices.
 * Sends commands into the Supabase `device_commands` table; the slave
 * firmware (CONTROL_MODE_WEB) polls and applies them.
 */
export const SlaveControls = () => {
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [loadingDevices, setLoadingDevices] = useState(true);

  // Local UI state — these are optimistic; the device is the source of truth
  // but Supabase doesn't push state back, so we just remember what we last sent.
  const [brightness, setBrightness] = useState(64);
  const [speed, setSpeed] = useState(0.2);
  const [power, setPower] = useState(true);
  const [accent, setAccent] = useState(true);
  const [sending, setSending] = useState<CommandType | null>(null);

  useEffect(() => {
    const fetchDevices = async () => {
      setLoadingDevices(true);
      const { data, error } = await supabase
        .from("devices")
        .select("device_id, name, location, hardware_type")
        .order("name");
      if (error) {
        toast.error("Failed to load devices");
      } else {
        setDevices(data ?? []);
        if (!selectedDevice && data && data.length > 0) {
          // Prefer a slave device by default, fall back to first
          const slave = data.find((d) => d.hardware_type === "ESP32 Slave");
          setSelectedDevice((slave ?? data[0]).device_id);
        }
      }
      setLoadingDevices(false);
    };
    fetchDevices();

    const channel = supabase
      .channel("slave-controls-devices")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "devices" },
        () => fetchDevices(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendCommand = async (
    type: CommandType,
    value: number | boolean,
  ) => {
    if (!selectedDevice) {
      toast.error("Pick a device first");
      return;
    }
    setSending(type);
    const { error } = await supabase.from("device_commands").insert([
      {
        device_id: selectedDevice,
        command_type: type,
        params: { value },
        processed: false,
      },
    ]);
    setSending(null);
    if (error) {
      toast.error(`Failed to send ${type}`, { description: error.message });
    } else {
      toast.success(`Sent ${type.replace("set_", "")} → ${String(value)}`);
    }
  };

  const deviceLabel = (d: DeviceRow) => {
    const short = formatDeviceId(d.device_id, true);
    const base = d.name || `Device ${short}`;
    return d.location ? `${base} (${d.location})` : base;
  };

  const slaves = useMemo(
    () => devices.filter((d) => d.hardware_type === "ESP32 Slave"),
    [devices],
  );
  const others = useMemo(
    () => devices.filter((d) => d.hardware_type !== "ESP32 Slave"),
    [devices],
  );

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Sliders className="mr-2 h-5 w-5" />
          Device Controls
        </CardTitle>
        <CardDescription>
          Web replacement for the physical encoders. Targets one device at a
          time. Requires firmware built with{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">
            CONTROL_MODE_WEB
          </code>
          .
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Device picker */}
        <div className="space-y-2">
          <Label>Target device</Label>
          <Select
            value={selectedDevice ?? ""}
            onValueChange={setSelectedDevice}
            disabled={loadingDevices}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={loadingDevices ? "Loading…" : "Select a device"}
              />
            </SelectTrigger>
            <SelectContent>
              {slaves.length > 0 && (
                <>
                  {slaves.map((d) => (
                    <SelectItem key={d.device_id} value={d.device_id}>
                      🟢 {deviceLabel(d)}
                    </SelectItem>
                  ))}
                </>
              )}
              {others.map((d) => (
                <SelectItem key={d.device_id} value={d.device_id}>
                  {deviceLabel(d)}
                </SelectItem>
              ))}
              {devices.length === 0 && !loadingDevices && (
                <SelectItem value="none" disabled>
                  No devices registered
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Power + Accent toggles */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="flex items-center gap-2">
              <Power className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="power-switch" className="cursor-pointer">
                LED strip power
              </Label>
            </div>
            <Switch
              id="power-switch"
              checked={power}
              onCheckedChange={(v) => {
                setPower(v);
                sendCommand("set_power", v);
              }}
              disabled={sending === "set_power"}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="accent-switch" className="cursor-pointer">
                Accent colors
              </Label>
            </div>
            <Switch
              id="accent-switch"
              checked={accent}
              onCheckedChange={(v) => {
                setAccent(v);
                sendCommand("set_accent", v);
              }}
              disabled={sending === "set_accent"}
            />
          </div>
        </div>

        {/* Brightness slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Sun className="h-4 w-4 text-muted-foreground" />
              Brightness
            </Label>
            <span className="font-mono text-sm text-muted-foreground">
              {brightness} / 255
            </span>
          </div>
          <Slider
            min={0}
            max={255}
            step={1}
            value={[brightness]}
            onValueChange={(v) => setBrightness(v[0])}
            onValueCommit={(v) => sendCommand("set_brightness", v[0])}
            disabled={!power}
          />
        </div>

        {/* Speed slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-muted-foreground" />
              Pattern speed
            </Label>
            <span className="font-mono text-sm text-muted-foreground">
              ×{speed.toFixed(1)}
            </span>
          </div>
          <Slider
            min={0.2}
            max={8}
            step={0.1}
            value={[speed]}
            onValueChange={(v) => setSpeed(v[0])}
            onValueCommit={(v) =>
              sendCommand("set_speed", Number(v[0].toFixed(2)))
            }
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>slow</span>
            <span>fast</span>
          </div>
        </div>

        {/* Quick presets */}
        <div className="flex flex-wrap gap-2 border-t pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBrightness(32);
              sendCommand("set_brightness", 32);
            }}
          >
            Dim
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBrightness(128);
              sendCommand("set_brightness", 128);
            }}
          >
            Medium
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBrightness(255);
              sendCommand("set_brightness", 255);
            }}
          >
            Full
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSpeed(0.2);
              sendCommand("set_speed", 0.2);
            }}
          >
            Slow speed
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSpeed(1);
              sendCommand("set_speed", 1);
            }}
          >
            Normal speed
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SlaveControls;
